import { useEffect, useRef, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { PageHeader } from '../../pages/AppShell';
import { Banner } from '../../components/ui';
import Icon from '../../components/Icon';

interface Turn { role: 'user' | 'assistant'; content: string; toolsUsed?: { name: string; input: Record<string, unknown> }[]; }
interface AskResult { answer: string; toolsUsed: { name: string; input: Record<string, unknown> }[]; }

const SUGGESTIONS = [
  'Who has checked in today?',
  'Who has not checked in yet?',
  'How many orders today?',
  'How many deliveries were completed today?',
  'How many visits were made this week?',
  'Top 5 customers this month',
  'Which product sold the most this month?',
  'Which customers have not been visited in 30 days?',
  'Who is on the road right now?',
  'Pending expense claims',
];

// What the assistant looked at, in plain words — so the reader can trust
// where a number came from without being shown a tool name.
const TOOL_LABEL: Record<string, string> = {
  attendance_status: 'attendance',
  orders_summary: 'orders',
  deliveries_summary: 'deliveries',
  visits_summary: 'visits',
  top_customers: 'customers',
  expenses_summary: 'expenses',
  team_members: 'team',
  customers_not_visited: 'customer visits',
  tracking_summary: 'tracking',
  product_sales: 'product sales',
};
function sourcesOf(tools: Turn['toolsUsed']): string {
  if (!tools?.length) return '';
  const parts = tools.map((t) => {
    const label = TOOL_LABEL[t.name] ?? t.name;
    const from = t.input?.from ?? t.input?.date;
    const to = t.input?.to;
    const when = from ? (to && to !== from ? ` ${from} → ${to}` : ` ${from}`) : '';
    return `${label}${when}`;
  });
  return `Based on: ${[...new Set(parts)].join(', ')}`;
}

export default function AssistantPage() {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [turns, busy]);

  async function send(text: string) {
    const message = text.trim();
    if (!message || busy) return;
    setError(null);
    setDraft('');
    const history = turns.slice(-10).map((t) => ({ role: t.role, content: t.content }));
    setTurns((cur) => [...cur, { role: 'user', content: message }]);
    setBusy(true);
    try {
      const r = await api.post<AskResult>('/assistant/ask', { message, history });
      setTurns((cur) => [...cur, { role: 'assistant', content: r.answer, toolsUsed: r.toolsUsed }]);
    } catch (err) {
      if (err instanceof ApiClientError && (err.code === 'ASSISTANT_NOT_CONFIGURED' || err.code === 'ASSISTANT_DISABLED')) setNotConfigured(true);
      setError(err instanceof ApiClientError ? err.message : 'Could not get an answer.');
      // Leave the question in the box so it can be retried.
      setDraft(message);
      setTurns((cur) => cur.slice(0, -1));
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  }

  return (
    <>
      <PageHeader
        title="Ask AI"
        subtitle="Ask about attendance, orders, deliveries, visits, customers and expenses — answered from your own data."
        actions={turns.length > 0 ? (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setTurns([]); setError(null); }}>New chat</button>
        ) : undefined}
      />
      <div className="page ai-page">
        {notConfigured && (
          <Banner kind="info">Ask AI is not switched on for this company yet. Contact Salestrendz to enable it.</Banner>
        )}

        <div className="ai-card">
          <div className="ai-thread">
            {turns.length === 0 && (
              <div className="ai-empty">
                <div className="ai-empty-mark"><Icon name="search" size={22} /></div>
                <h3>What would you like to know?</h3>
                <p className="faint">Try one of these, or type your own question.</p>
                <div className="ai-chips">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} type="button" className="ai-chip" onClick={() => send(s)} disabled={busy}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            {turns.map((t, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className={`ai-msg ${t.role}`}>
                <div className="ai-bubble">
                  {t.content.split('\n').map((line, j) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <p key={j}>{line}</p>
                  ))}
                  {t.role === 'assistant' && t.toolsUsed?.length ? (
                    <div className="ai-src">{sourcesOf(t.toolsUsed)}</div>
                  ) : null}
                </div>
              </div>
            ))}

            {busy && (
              <div className="ai-msg assistant">
                <div className="ai-bubble ai-typing" aria-label="Thinking"><i /><i /><i /></div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {error && <div className="ai-error"><Icon name="alert" size={13} /> {error}</div>}

          <form
            className="ai-composer"
            onSubmit={(e) => { e.preventDefault(); void send(draft); }}
          >
            <input
              ref={inputRef}
              className="input ai-input"
              placeholder="e.g. Who has checked in today?"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={1000}
              disabled={busy}
              autoFocus
            />
            <button type="submit" className="btn btn-primary" disabled={busy || !draft.trim()}>Ask</button>
          </form>
          {turns.length > 0 && (
            <div className="ai-chips ai-chips--follow">
              {SUGGESTIONS.slice(0, 4).map((s) => (
                <button key={s} type="button" className="ai-chip" onClick={() => send(s)} disabled={busy}>{s}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
